import { lazy, type LazyExoticComponent, type ComponentType } from "react";

export type SectionPageConfig = {
  slug: string;
  label: string;
  group: "Platform" | "Trust" | "Investors" | "Company";
  title: string;
  description: string;
  Component: LazyExoticComponent<ComponentType<unknown>>;
};

export const sectionPages: SectionPageConfig[] = [
  {
    slug: "how-it-works",
    label: "How it Works",
    group: "Platform",
    title: "How AIMediQ™ Works | datasyteAI™",
    description:
      "See the Code Blue workflow step by step: how AIMediQ™ powered by datasyteAI™ supports clinicians from initiation to handoff.",
    Component: lazy(() => import("@/components/HowItWorks").then((m) => ({ default: m.HowItWorks }))),
  },
  {
    slug: "aimediq",
    label: "AIMediQ™",
    group: "Platform",
    title: "AIMediQ™ — The AI-Powered Code Cart | datasyteAI™",
    description:
      "AIMediQ™ is the world's first AI-powered Code Cart, powered by datasyteAI™, built for clarity under pressure.",
    Component: lazy(() => import("@/components/SolutionSection").then((m) => ({ default: m.SolutionSection }))),
  },
  {
    slug: "technology",
    label: "Technology",
    group: "Platform",
    title: "Product & Technology Overview | datasyteAI™",
    description:
      "Product and technology overview for AIMediQ™ powered by datasyteAI™, including architecture and patent information.",
    Component: lazy(() => import("@/components/TechnologySection").then((m) => ({ default: m.TechnologySection }))),
  },
  {
    slug: "train",
    label: "Train",
    group: "Platform",
    title: "Trained to Your Experts & Protocols | datasyteAI™",
    description:
      "datasyteAI™ is trained to your institution's experts, protocols, and standards of care.",
    Component: lazy(() => import("@/components/TrainSection").then((m) => ({ default: m.TrainSection }))),
  },
  {
    slug: "benefits",
    label: "Benefits",
    group: "Platform",
    title: "What Improves with datasyteAI™ | AIMediQ™",
    description:
      "What improves with datasyteAI™: speed, consistency, documentation, and confidence during a Code Blue.",
    Component: lazy(() => import("@/components/BenefitsGallery").then((m) => ({ default: m.BenefitsGallery }))),
  },
  {
    slug: "video",
    label: "Video",
    group: "Platform",
    title: "What Is a Code Blue? Video Explainer | datasyteAI™",
    description: "A short video explainer on what a Code Blue is and why response quality varies.",
    Component: lazy(() => import("@/components/VideoSection").then((m) => ({ default: m.VideoSection }))),
  },
  {
    slug: "flip-book",
    label: "Interactive Flip Book",
    group: "Company",
    title: "Interactive Flip Book | AIMediQ™ powered by datasyteAI™",
    description:
      "Explore our interactive flip book to learn more about AIMediQ™ powered by datasyteAI™.",
    Component: lazy(() => import("@/components/FlipBookSection").then((m) => ({ default: m.FlipBookSection }))),
  },
  {
    slug: "problem",
    label: "Problem",
    group: "Trust",
    title: "The Problem — Why Code Blue Outcomes Vary | datasyteAI™",
    description: "Why Code Blue outcomes vary across teams, shifts, and facilities.",
    Component: lazy(() => import("@/components/ProblemSection").then((m) => ({ default: m.ProblemSection }))),
  },
  {
    slug: "guardrails",
    label: "Guardrails",
    group: "Trust",
    title: "Security, Privacy & Safety Guardrails | datasyteAI™",
    description:
      "Security, privacy, and safety limits behind AIMediQ™, including quantum-resistant security work in progress.",
    Component: lazy(() => import("@/components/SecuritySection").then((m) => ({ default: m.SecuritySection }))),
  },
  {
    slug: "regulatory-status",
    label: "Regulatory Status",
    group: "Trust",
    title: "Regulatory Status | AIMediQ™ powered by datasyteAI™",
    description:
      "AIMediQ™ powered by datasyteAI™ is in the pre-market development and validation phase. Read the regulatory status.",
    Component: lazy(() => import("@/components/ComplianceSection").then((m) => ({ default: m.ComplianceSection }))),
  },
  {
    slug: "market",
    label: "Market",
    group: "Investors",
    title: "Market Opportunity | datasyteAI™",
    description: "Market size and opportunity for AI-assisted resuscitation decision support.",
    Component: lazy(() => import("@/components/MarketSection").then((m) => ({ default: m.MarketSection }))),
  },
  {
    slug: "business-model",
    label: "Model",
    group: "Investors",
    title: "Business Model & Financial Strategy | datasyteAI™",
    description: "Business model and financial strategy for AIMediQ™ powered by datasyteAI™.",
    Component: lazy(() => import("@/components/BusinessSection").then((m) => ({ default: m.BusinessSection }))),
  },
  {
    slug: "competition",
    label: "Competition",
    group: "Investors",
    title: "Competitive Landscape | datasyteAI™",
    description: "How AIMediQ™ powered by datasyteAI™ compares to existing approaches.",
    Component: lazy(() => import("@/components/CompetitionSection").then((m) => ({ default: m.CompetitionSection }))),
  },
  {
    slug: "contact",
    label: "Contact",
    group: "Company",
    title: "Contact PathFree Technologies | datasyteAI™",
    description: "Get in touch with PathFree Technologies Corporation about AIMediQ™ and datasyteAI™.",
    Component: lazy(() => import("@/components/ContactSection").then((m) => ({ default: m.ContactSection }))),
  },
];

export const sectionPageBySlug = Object.fromEntries(
  sectionPages.map((page) => [page.slug, page])
) as Record<string, SectionPageConfig>;
